// @file Composable to scroll to a section
import { dir } from '@@/bits/current_dir'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Id } from '@@/types'
import { storeToRefs } from 'pinia'
import { nextTick } from 'vue'

export function useScrollToSection(): {
  scrollToSection: typeof scrollToSection
} {
  const { isGrid, isMap, isTimelineV1 } = storeToRefs(useSurfaceStore())
  const { isSmallerThanTabletLandscape } = storeToRefs(useWindowSizeStore())

  const scrollToSection = (
    sectionId: Id | string,
    options: { smooth?: boolean; centralize?: boolean } = { smooth: false, centralize: false },
  ): void => {
    nextTick(() => {
      const sectionEl = document.getElementById(`section-${sectionId}`)
      if (sectionEl == null) return
      let containerId = 'wall-container'
      if (isMap.value) {
        containerId = 'surface-map-post-list'
      } else if (isTimelineV1.value) {
        containerId = 'wish-list'
      }
      const containerEl = document.getElementById(containerId)
      if (containerEl == null) return

      let xCoordinate = sectionEl.offsetLeft
      let yCoordinate = sectionEl.offsetTop
      const scrollDirection =
        isGrid.value || (isMap.value && isSmallerThanTabletLandscape.value) || isTimelineV1.value
          ? 'horizontal'
          : 'vertical'
      if (options.centralize === true) {
        if (scrollDirection === 'horizontal') {
          const extraSpace = Math.max(containerEl.clientWidth - sectionEl.scrollWidth, 0)
          xCoordinate = xCoordinate - extraSpace / 2
        } else {
          const extraSpace = Math.max(containerEl.clientHeight - sectionEl.scrollHeight, 0)
          yCoordinate = yCoordinate - extraSpace / 2
        }
      }

      const originalScrollBehavior = containerEl.style.scrollBehavior
      containerEl.style.scrollBehavior = options.smooth === true ? 'smooth' : 'auto'
      if (scrollDirection === 'horizontal') {
        if (isTimelineV1.value) {
          // In Timeline V1 with 'rtl' direction, we need to calculate the scroll position differently
          // when scrolling to a section via keyboard shortcuts (Shift+ArrowLeft/Shift+ArrowRight).
          if (dir() === 'rtl') {
            const sectionRight = sectionEl.offsetLeft + sectionEl.offsetWidth
            const scrollLeft = sectionRight - containerEl.clientWidth
            containerEl.scroll(scrollLeft, 0)
          } else {
            containerEl.scroll(xCoordinate, 0)
          }

          // Focus the first button inside the section for better accessibility
          const firstButton = sectionEl.querySelector('button')
          if (firstButton != null) {
            firstButton.focus({ preventScroll: true })
          }
        } else {
          containerEl.scroll(xCoordinate, 0)
        }
      } else {
        containerEl.scroll(0, yCoordinate)
      }
      containerEl.style.scrollBehavior = originalScrollBehavior
    })
  }

  return {
    scrollToSection,
  }
}
